import { reinterpretYearMonthDayAsLocalTime } from './reinterpretYearMonthDayAsLocalTime'

/**
 * Safe date parsing utility that handles various date string formats
 * and provides iOS Safari compatibility
 */
export interface SafeDateParseResult {
  date: Date
  isValid: boolean
  originalFormat?: string
  error?: string
}

/**
 * Detects and safely parses date strings in various formats, with special handling for iOS Safari
 * Supports: MM-DD-YYYY, MM/DD/YYYY, YYYY-MM-DD, ISO strings
 *
 * @param input - Date string
 * @returns SafeDateParseResult with parsed date and validation info
 */
export const safeDateParse = (input: string): SafeDateParseResult => {
  // Handle string inputs
  const dateString = input.trim()

  if (!dateString) {
    return {
      date: new Date(NaN),
      isValid: false,
      error: 'Empty date string',
    }
  }

  // Try ISO format first (most reliable)
  if (
    dateString.includes('T') ||
    dateString.includes('Z') ||
    dateString.match(/^\d{4}-\d{2}-\d{2}$/)
  ) {
    const date = new Date(dateString)
    if (!isNaN(date.getTime())) {
      return {
        date,
        isValid: true,
        originalFormat: 'ISO/YYYY-MM-DD',
      }
    }
  }

  // Handle both MM-DD-YYYY and MM/DD/YYYY formats
  const dateMatch = dateString.match(/^(\d{1,2})([-\/])(\d{1,2})\2(\d{4})$/)

  if (dateMatch) {
    const [, month, separator, day, year] = dateMatch
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day))

    // Validate the parsed date makes sense
    if (
      date.getFullYear() === parseInt(year) &&
      date.getMonth() === parseInt(month) - 1 &&
      date.getDate() === parseInt(day)
    ) {
      return {
        date,
        isValid: true,
        originalFormat: `MM${separator}DD${separator}YYYY`,
      }
    }
  }

  // Fallback to native Date constructor with error handling
  try {
    const date = new Date(dateString)
    if (!isNaN(date.getTime())) {
      return {
        date,
        isValid: true,
        originalFormat: 'native parsing',
      }
    }
  } catch (error) {
    console.error(
      `An error occured attempting to create a date from string: ${dateString}`,
      error
    )
  }

  // All parsing attempts failed
  return {
    date: new Date(NaN),
    isValid: false,
    error: `Unable to parse date string: "${dateString}"`,
  }
}

/**
 * Safe wrapper for creating Date objects from potentially problematic date strings
 * Returns a valid Date object or throws a descriptive error
 *
 * @param input - Date string
 * @returns Valid Date object
 * @throws Error if parsing fails
 */
export const createSafeDate = (input: string): Date => {
  const result = safeDateParse(input)

  if (!result.isValid) {
    throw new Error(result.error || `Invalid date: ${input}`)
  }

  return result.date
}

/**
 * Safe wrapper that returns a Date object or null if parsing fails
 * Useful when you want to handle invalid dates gracefully
 *
 * @param input - Date string
 * @returns Valid Date object or null
 */
export const createSafeDateOrNull = (input: string): Date | null => {
  const result = safeDateParse(input)
  return result.isValid ? result.date : null
}

/** Use default locale and timeZone as UTC to account for the difference of the
 *  UTC date stored in the database and the locale timezone when formatting for
 *  program details display.
 *  isUTC param will be use to convert a date into UTC and then convert it into a string
 */
export const dateFormatter = new Intl.DateTimeFormat()
const GET_ONLY_DATE_PART_FROM_STRING = 10
export const IS_UTC = true
const standardOptions: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
}
export const dateTimeFormatterUS = new Intl.DateTimeFormat(
  'en-US',
  standardOptions
)

export const dateToDashString = (date: Date, isUTC?: boolean): string => {
  if (isUTC) {
    const dateArray = date
      .toISOString()
      .substring(0, GET_ONLY_DATE_PART_FROM_STRING)
      .split('-')

    return [dateArray[1], dateArray[2], dateArray[0]].join('-')
  } else {
    const dateParts = convertDateFromLocaleToDefault(date)

    const dateArray = dateParts.split('/').map((it) => {
      if (parseInt(it) < 10) {
        it = `0${parseInt(it)}`
      }
      return it
    })

    return [dateArray[0], dateArray[1], dateArray[2]].join('-')
  }
}

/**
 *  @param date
 * Use Date.UTC to account for the difference of the
 *  date stored in the database and the locale timezone offset
 *  when formatting for program details display.
 * e.g. providing `Tue Aug 01 2023 10:00:00 GMT+1000 (Australian Eastern Standard Time)`
 * returns `Tue Aug 01 2023 00:00:00 GMT+1000 (Australian Eastern Standard Time)`
 */
export const getDateWithZeroHourOffset = (date: Date): Date => {
  return new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
  )
}
/**
 *
 * @deprecated
 * We will no longer use the 'MM/DD/YYYY' date format directly.
 * All dates should now be formatted using `dateToDashString` for consistency
 * across the application.
 * Transform a Date in an string with format `MM/DD/YYYY`
 * @param date - `Date` Date that will be transform to a string
 * @param isUTC - `boolean` if is false or omitted, transform the date in your local time zone,
 * if is true, transform the date as it come from DB
 * @returns a date converted to a string
 */

export const dateToSlashString = (date: Date, isUTC?: boolean): string => {
  if (isUTC) {
    const dateArray = date
      .toISOString()
      .substring(0, GET_ONLY_DATE_PART_FROM_STRING)
      .split('-')
    return [dateArray[1], dateArray[2], dateArray[0]].join('/')
  } else {
    const dateParts = convertDateFromLocaleToDefault(date)

    const dateArray = dateParts.split('/').map((it) => {
      if (parseInt(it) < 10) {
        it = `0${parseInt(it)}`
      }
      return it
    })
    return [dateArray[0], dateArray[1], dateArray[2]].join('/')
  }
}

const options: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
}
export const dateTimeFormatter = new Intl.DateTimeFormat('en-US', options)

/**
 * Utility function that takes in a UTC date and formats to a string for use in a native date-time picker (type="datetime-local").
 * @param {Date} date - A date in UTC time (or ISO format)
 * @returns a string in the following format: "yyyy-MM-ddThh:mm"
 */
export const dateWithTimeToDashString = (date: Date): string => {
  const [separatedDate, separatedTime] = dateTimeFormatter
    .format(date)
    .split(',')

  const [mm, dd, yyyy] = separatedDate.split('/')
  const time = separatedTime.trim()

  return `${yyyy}-${mm}-${dd}T${time}`
}

/**
 * /**
 * @deprecated
 * We will no longer use the 'MM/DD/YYYY' date format directly.
 * please use format MM-DD-YYY for consistency
 * across the application.
 *
 * Formats a Date object into a string representation in the format "MM/DD/YYYY HH:MM:SSZ",
 * where the time components are expressed in Coordinated Universal Time (UTC).
 *
 * @param {Date} date The Date object to format.
 * @returns {string} The formatted date and time string in "MM/DD/YYYY HH:MM:SSZ" UTC format.
 *
 * @example
 * const now = new Date();
 * const utcString = dateWithTimeToSlashString(now);
 * console.log(utcString); // Output will be something like "04/11/2025 22:12:00Z" (depending on the current UTC time)
 */
export const dateWithTimeToSlashString = (date: Date): string => {
  const pad = (num: number): string => num.toString().padStart(2, '0')

  const mm = pad(date.getUTCMonth() + 1) // Month is 0-indexed
  const dd = pad(date.getUTCDate())
  const yyyy = date.getUTCFullYear()

  const hh = pad(date.getUTCHours())
  const min = pad(date.getUTCMinutes())
  const ss = pad(date.getUTCSeconds())

  return `${mm}/${dd}/${yyyy} ${hh}:${min}:${ss}Z`
}

export const parsedDate = (dateString: string): number => {
  return Date.parse(dateString)
}

export const isValidDateString = (dateString: string): boolean => {
  const result = safeDateParse(dateString)
  return result.isValid
}

export const isFirstDateStringBeforeSecond = (
  firstDate: Date,
  secondDate: Date
): boolean => {
  return (
    parsedDate(firstDate.toUTCString()) < parsedDate(secondDate.toUTCString())
  )
}

export const isFirstDateStringBeforeOrAtSecond = (
  firstDate: Date,
  secondDate: Date
): boolean => {
  return (
    parsedDate(firstDate.toUTCString()) <= parsedDate(secondDate.toUTCString())
  )
}

/**
 * Safe version of isFirstDateStringBeforeSecond that handles problematic date strings
 * Uses safeDateParse to handle iOS Safari compatibility issues
 */
export const safeIsFirstDateStringBeforeSecond = (
  firstDate: string | Date,
  secondDate: string | Date
): boolean => {
  const first =
    firstDate instanceof Date ? firstDate : createSafeDate(firstDate)
  const second =
    secondDate instanceof Date ? secondDate : createSafeDate(secondDate)

  return isFirstDateStringBeforeSecond(first, second)
}

/**
 * Safe version of isFirstDateStringBeforeOrAtSecond that handles problematic date strings
 * Uses safeDateParse to handle iOS Safari compatibility issues
 */
export const safeIsFirstDateStringBeforeOrAtSecond = (
  firstDate: string | Date,
  secondDate: string | Date
): boolean => {
  const first =
    firstDate instanceof Date ? firstDate : createSafeDate(firstDate)
  const second =
    secondDate instanceof Date ? secondDate : createSafeDate(secondDate)

  return isFirstDateStringBeforeOrAtSecond(first, second)
}

export const isSameDate = (firstDate: Date, secondDate: Date): boolean => {
  return (
    parsedDate(firstDate.toUTCString()) === parsedDate(secondDate.toUTCString())
  )
}

export const dayOfTheWeekAsStringLiteral = (date: Date): string => {
  return Intl.DateTimeFormat('en-US', { weekday: 'long' }).format(date)
}

export const isWithinDateRange = (
  range: {
    start: Date
    end: Date
  },
  dateInQuestion: Date
): boolean => {
  return (
    dateUtilities.isFirstDateStringBeforeOrAtSecond(
      range.start,
      dateInQuestion
    ) &&
    dateUtilities.isFirstDateStringBeforeOrAtSecond(dateInQuestion, range.end)
  )
}

/**
 * @deprecated
 * We will no longer use the 'MM/DD/YYYY' date format directly.
 * All dates should now be formatted using `dateToDashString` with is utc set to true for consistency
 * across the application.
 */
export const dateToSlashStringReinterpretedAsLocal = (date: Date): string => {
  return dateToSlashString(reinterpretYearMonthDayAsLocalTime(date))
}

export const dateToDashStringReinterpretedAsLocal = (date: Date): string => {
  return dateToDashString(reinterpretYearMonthDayAsLocalTime(date))
}

/**
 * Takes in a date and transforms it into a string in format yyyy-mm-dd in UTC
 *
 * @param {Date} date Any date object
 * @returns {String} yyyy-mm-dd format
 */
export const utcDateString = (date: Date): string => {
  try {
    return date.toISOString().split('T')[0]
  } catch (err) {
    return new Date().toDateString().split('T')[0]
  }
}

export const convertDateFromLocaleToDefault = (
  date: Date,
  locale: string = Intl.DateTimeFormat().resolvedOptions().locale
): string => {
  /*
    if a date is not UTC but has international format, 
    let's make the format en-US not avoid failure 
    */
  return locale !== 'en-US'
    ? dateTimeFormatterUS.format(date)
    : dateFormatter.format(date)
}

/**
 * Formats a given `Date` object into a human-readable string in the format:
 * "Weekday, Month Day, Year". The formatting is based on the 'en-US' locale
 * and ensures the date is represented in UTC timezone.
 *
 * @param {Date} date - The `Date` object to format.
 * @returns {string} - A string representing the formatted date, e.g.,
 * "Friday, August 13, 2021".
 *
 * @example
 * const date = new Date('2021-08-13T12:00:00Z');
 * console.log(formatFullDateString(date));
 * // Output: "Friday, August 13, 2021"
 */
export const formatFullDateString = (date: Date): string => {
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  })
}

const dateUtilities = {
  dateToDashString,
  isValidDateString,
  parsedDate,
  isFirstDateStringBeforeSecond,
  isFirstDateStringBeforeOrAtSecond,
  safeIsFirstDateStringBeforeSecond,
  safeIsFirstDateStringBeforeOrAtSecond,
  isWithinDateRange,
  dateToSlashStringReinterpretedAsLocal,
  utcDateString,
  convertDateFromLocaleToDefault,
  dateTimeFormatterUS,
  dateFormatter,
  formatFullDateString,
  safeDateParse,
  createSafeDate,
  createSafeDateOrNull,
}

export default dateUtilities

/**
 * Returns the year part of a date as a string in UTC.
 *
 * @param {Date} date - The date object from which to extract the year.
 * @returns {string} The year in UTC as a string.
 */
export const UTCYearString = (date: Date): string => {
  return String(date.getUTCFullYear())
}
